import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventsProvider } from './contexts/EventsContext';
import AppLayout from './components/Layout/AppLayout';
import { DashboardReportProvider } from './contexts/DashboardReportContext';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import Cases from './components/Cases/Cases';
import CounselorsManagement from './components/Counselors/CounselorsManagement';
import CalendarManagement from './components/Calendar/CalendarManagement';
import SessionReportsPage from './components/SessionReports/SessionReportsPage';
import MeetingNotesPage from './components/MeetingNotes/MeetingNotesPage';
import MonthlyReportPage from './components/MonthlyReport/MonthlyReportPage';
import ResourcesPage from './components/Resources/ResourcesPage';
import AdminTools from './components/Admin/AdminTools';
import MyProfile from './components/Profile/MyProfile';
import RoleUpdateNotifier from './components/Auth/RoleUpdateNotifier';
import InstallPwaBanner from './components/Layout/InstallPwaBanner';
import { syncAppBadge } from './utils/pwa';
import { syncFcmToken } from './utils/pushNotifications';
import { t } from './utils/translations';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#C99700',
      light: '#FEF3C7',
      dark: '#B89A00',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#666666',
      light: '#999999',
      dark: '#333333',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

/**
 * Deliberately identical to the boot screen inlined in `index.html`: same white ground, same
 * logo at the same size. React mounting then replaces one with the other without a visible
 * change, so the launch reads as a single continuous screen rather than three.
 */
const BootScreen: React.FC = () => (
  <div
    role="status"
    aria-label={t.common.loading}
    className="flex min-h-[100dvh] items-center justify-center bg-white"
  >
    <img
      src="/icons/icon-512.png"
      alt=""
      className="w-[min(40vw,180px)] animate-pulse motion-reduce:animate-none"
    />
  </div>
);

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <BootScreen />;
  }

  return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

// Main App component
const AppContent: React.FC = () => {
  const { currentUser, loading } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      syncAppBadge(0);
      return;
    }
    void syncFcmToken(currentUser.id);
  }, [currentUser]);

  if (loading) {
    return <BootScreen />;
  }

  const routes = (
    <Routes>
      <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/" />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <Cases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/counselors"
        element={
          <ProtectedRoute>
            <CounselorsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session-reports"
        element={
          <ProtectedRoute>
            <SessionReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meeting-notes"
        element={
          <ProtectedRoute>
            <MeetingNotesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly-report"
        element={
          <ProtectedRoute>
            <MonthlyReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <ResourcesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminTools />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  if (!currentUser) {
    return routes;
  }

  return (
    <EventsProvider>
      <DashboardReportProvider>
        <RoleUpdateNotifier />
        <AppLayout>{routes}</AppLayout>
      </DashboardReportProvider>
    </EventsProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <InstallPwaBanner />
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;